// "Up next" customer preview card (approved `section.next`, aria-label
// "Next customer preview").
//
// Two renderers share the approved markup:
//
//   <NextCustomer />       design-preview fixture (MICHAEL). W32: renders
//                          nothing on a real session; preview and the
//                          design harness are unchanged.
//   <QueueNextCustomer />  W40.1 (2026-10-21, M2): the same card hydrated
//                          from the W27 rep queue lookahead
//                          (GET /v1/reps/:repId/queue/lookahead?n=3, read
//                          by useRepQueue). Shows the next one to three
//                          real contacts with their first why-line, or an
//                          honest note: "Loading the queue…", "Queue is
//                          empty." (200 with an empty lookahead), the
//                          api-server error code, or the degraded reason
//                          when the engine is not wired. No timestamp and
//                          no trigger are invented: the reason block
//                          carries the queue count and the approved
//                          "Queue updates as customer needs change" line.
import type { ReactNode } from 'react';
import { Icon } from './IconSprite.js';
import type { MockCustomer } from '../../data/mockData.js';
import { useIsRealSession } from './PriorityFocus.js';
import type { QueueItem, QueueStatus } from './useRepQueue.js';

export interface NextCustomerProps {
  customer: MockCustomer;
  summary: string;
  trigger: string;
  age: string;
}

export function NextCustomer({ customer, summary, trigger, age }: NextCustomerProps): ReactNode {
  const realSession = useIsRealSession();
  if (realSession) return null;
  return (
    <section className="next" data-component="next-customer-preview" aria-label="Next customer preview">
      <span className="eyebrow">Up next</span>
      <span className="avatar">{customer.initials}</span>
      <div>
        <strong>{customer.fullName}</strong>
        <p>{summary}</p>
      </div>
      <div className="reason">
        <strong style={{ fontSize: 11, fontWeight: 400 }}>
          {trigger} · {age}
        </strong>
        <p>Queue updates as customer needs change</p>
      </div>
      <Icon id="arrow" />
    </section>
  );
}

// ---------------------------------------------------------------------
// W40.1: queue-hydrated card for real sessions.
// ---------------------------------------------------------------------

export const QUEUE_EMPTY_NOTE = 'Queue is empty.';
export const QUEUE_LOADING_NOTE = 'Loading the queue…';
export const QUEUE_UPDATES_NOTE = 'Queue updates as customer needs change';

export interface QueueNextCustomerProps {
  status: QueueStatus;
  items: QueueItem[];
  /** Reason string when the api-server reports the engine is not wired, or the error code. */
  degraded: string | null;
  /** Upper bound on the contacts shown (the lookahead read asks for 3). */
  max?: number;
}

export function queueItemInitials(item: QueueItem): string {
  const fromDisplay = item.display?.initials;
  if (typeof fromDisplay === 'string' && fromDisplay.length > 0) return fromDisplay;
  const name = item.display?.name ?? '';
  const parts = name.split(/\s+/).filter(Boolean);
  const out = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
  return out || item.customerId.slice(0, 2).toUpperCase();
}

export function queueItemName(item: QueueItem): string {
  return item.display?.name ?? `Contact ${item.customerId}`;
}

export function queueItemWhy(item: QueueItem): string {
  return item.why[0] ?? 'No reason attached';
}

export function QueueNextCustomer(props: QueueNextCustomerProps): ReactNode {
  const { status, items, degraded, max = 3 } = props;
  // Preview / dev-mock sessions report 'skipped': the fixture card owns
  // that path, this one renders nothing.
  if (status === 'skipped') return null;
  const shown = items.slice(0, max);

  let note: string | null = null;
  if (status === 'loading') note = QUEUE_LOADING_NOTE;
  else if (status === 'error') note = `Queue preview could not load (${degraded ?? 'ERR'}).`;
  else if (degraded) note = `Queue engine not wired (${degraded}).`;
  else if (shown.length === 0) note = QUEUE_EMPTY_NOTE;

  return (
    <section
      className="next"
      data-component="next-customer-preview"
      data-mode="queue"
      data-testid="up-next-card"
      aria-label="Next customer preview"
    >
      <span className="eyebrow">Up next</span>
      {note ? (
        <div>
          <strong data-testid="up-next-note">{note}</strong>
          <p>{QUEUE_UPDATES_NOTE}</p>
        </div>
      ) : (
        <>
          {shown.map((item) => (
            <div className="next-entry" key={item.customerId} data-testid="up-next-item">
              <span className="avatar">{queueItemInitials(item)}</span>
              <div>
                <strong>{queueItemName(item)}</strong>
                <p>{queueItemWhy(item)}</p>
              </div>
            </div>
          ))}
          <div className="reason">
            <strong style={{ fontSize: 11, fontWeight: 400 }}>
              {shown.length === 1 ? 'Next in your queue' : `Next ${shown.length} in your queue`}
            </strong>
            <p>{QUEUE_UPDATES_NOTE}</p>
          </div>
        </>
      )}
      <Icon id="arrow" />
    </section>
  );
}
